import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetAccountByIdQuery } from '../../../../../../services/AccountService';
import { useCreateWriteoffMutation } from '../../../../../../services/WriteoffService';
import { createWriteoffRequestFromAccount } from '../../../../../../services/WriteoffService/requests/writeoffCreateRequest';

export interface UseAccountSummaryOptions {
  accountId: string | null;
  open: boolean;
  onClose: () => void;
}

export interface UseAccountSummaryReturn {
  account: any;
  isLoading: boolean;
  error: any;
  isProcessing: boolean;
  isCreating: boolean;
  errorMessage: string | null;
  handleCreateWriteOff: () => Promise<void>;
  handleStillCollectible: () => void;
  handleCloseError: () => void;
}

export function useAccountSummary({
  accountId,
  open,
  onClose,
}: UseAccountSummaryOptions): UseAccountSummaryReturn {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    data: account,
    isLoading,
    error,
  } = useGetAccountByIdQuery(accountId || '', {
    skip: !open || !accountId,
  });

  const [createWriteoff, { isLoading: isCreating }] = useCreateWriteoffMutation();

  const handleCreateWriteOff = useCallback(async () => {
    if (!accountId || !account) return;

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const company = searchParams.get('company') || '';
      const center = searchParams.get('center') || '';

      // Create the writeoff request from account data
      const request = createWriteoffRequestFromAccount(account, company, center);

      // Call the API to create the writeoff
      const result = await createWriteoff(request).unwrap();

      // Navigate to the edit page with the new writeoff ID
      const writeoffId = result.identifiers?.id || result.identifiers?.caseId;
      navigate(
        `/writeoffs/edit?company=${company}&center=${center}&id=${writeoffId}&accountId=${accountId}`
      );
      onClose();
    } catch (err) {
      console.error('Failed to create writeoff:', err);
      setErrorMessage('Failed to create write-off. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [accountId, account, searchParams, createWriteoff, navigate, onClose]);

  const handleStillCollectible = useCallback(() => {
    if (!accountId) return;

    console.log('Still Collectible selected for account:', accountId);
    // TODO: Implement still collectible logic
    onClose();
  }, [accountId, onClose]);

  const handleCloseError = useCallback(() => {
    setErrorMessage(null);
  }, []);

  return {
    account,
    isLoading,
    error,
    isProcessing,
    isCreating,
    errorMessage,
    handleCreateWriteOff,
    handleStillCollectible,
    handleCloseError,
  };
}
